import { preloadAttachmentBlockOnSend } from 'owa-attachment-block-on-send';
import type AttachmentWellHost from 'owa-calendar-meeting-forms-common-store/lib/store/schema/AttachmentWellHost';
import type { ClientAttachmentId, ClientItemId } from 'owa-client-ids';
import {
    lazyCreateAttachmentsViaQueueManager,
    AttachmentFileList,
    CreateAttachmentsProperties,
    OnAttachmentCreatedCallback,
    OnAttachmentCanceledOrFailedCallback,
} from 'owa-attachment-well-data';
import type { InfoBarHostViewState } from 'owa-info-bar/lib/schema/InfoBarMessageViewState';
import { lazyProcessInlineImageBeforeUpload } from 'owa-inline-image';
import getFullComposeViewState from '../selectors/getFullComposeViewState';
import { isFeatureEnabled } from 'owa-feature-flags';

function createAttachmentsHandler(
    attachmentWellHost: AttachmentWellHost,
    parentItemId: ClientItemId,
    files: AttachmentFileList,
    createAttachmentProperties: CreateAttachmentsProperties,
    infoBar?: InfoBarHostViewState,
    onFinish?: (attachmentId: ClientAttachmentId) => void,
    onAttachmentCreatedCallback?: OnAttachmentCreatedCallback,
    onAttachmentCanceledOrFailedCallback?: OnAttachmentCanceledOrFailedCallback,
    tryAgainCallback?: () => void
) {
    preloadAttachmentBlockOnSend.import();
    lazyCreateAttachmentsViaQueueManager.import().then(createAttachmentsViaQueueManager => {
        const filesQueuedToAttach = createAttachmentsViaQueueManager(
            attachmentWellHost.attachmentWell,
            parentItemId,
            files,
            infoBar,
            createAttachmentProperties,
            onFinish,
            onAttachmentCreatedCallback,
            onAttachmentCanceledOrFailedCallback,
            tryAgainCallback
        );

        const viewState = getFullComposeViewState();
        if (
            createAttachmentProperties.isInline &&
            viewState &&
            !isFeatureEnabled('cmp-inlineImageV2')
        ) {
            const fileLength = filesQueuedToAttach.length;
            const inlineAttachmentsLength =
                attachmentWellHost.attachmentWell.inlineAttachments.length;
            lazyProcessInlineImageBeforeUpload.import().then(processInlineImageBeforeUpload => {
                for (let i = 0; i < fileLength; i++) {
                    // Get the last fileLength number of inline attachments
                    // since they are the new ones created from above createAttachmentsViaQueueManager.
                    const attachmentState =
                        attachmentWellHost.attachmentWell.inlineAttachments[
                            inlineAttachmentsLength - fileLength + i
                        ];

                    processInlineImageBeforeUpload(
                        viewState.inlineImage,
                        viewState.bodyViewState,
                        attachmentState.placeholderId,
                        filesQueuedToAttach[i]
                    );
                }
            });
        }
    });
}

export default function createAttachments(
    attachmentWellHost: AttachmentWellHost,
    parentItemId: ClientItemId,
    files: AttachmentFileList,
    createAttachmentProperties: CreateAttachmentsProperties,
    infoBar?: InfoBarHostViewState,
    onFinish?: (attachmentId: ClientAttachmentId) => void,
    onAttachmentCreatedCallback?: OnAttachmentCreatedCallback,
    onAttachmentCanceledOrFailedCallback?: OnAttachmentCanceledOrFailedCallback,
    tryAgainCallback?: () => void
) {
    createAttachmentsHandler(
        attachmentWellHost,
        parentItemId,
        files,
        createAttachmentProperties,
        infoBar,
        onFinish,
        onAttachmentCreatedCallback,
        onAttachmentCanceledOrFailedCallback,
        tryAgainCallback
    );
}
